import { forwardRef, useMemo } from 'react'
import { observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  Block,
  Checkbox,
  DateInput,
  Heading,
  MultiSelect,
  SingleSelect,
} from 'suomifi-ui-components'
import { isValid, parseISO } from 'date-fns'
import { Nullable } from 'common/src/vtj/types/nullable.type'
import {
  useCountrySelectData,
  selectedMultiItems,
  selectedSingleItem,
} from 'todistukset-common/src/vtj/ui/country-select.util'
import {
  EditableField,
  Syntymapaiva,
  finalEditableFieldValue,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { formatDateFormatToUiDateFormat } from 'todistukset-common/src/vtj/util/date.util'
import {
  validateSpouseBFirstNames,
  validateSpouseBLastName,
  validateSpouseBBirthDate,
  validateSpouseBCitizenshipCode,
  validateSpouseBBirthPlace,
  validateSpouseBBirthCountry,
  validateSpouseBMaritalStatus,
  validateSpouseNotRelated,
} from 'todistukset-common/src/vtj/uaet-validation'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import {
  FormContainer,
  InfoInlineAlert,
  StyledTextInput,
} from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString, {
  LocalizedParagraphs,
} from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { TextInputColumns } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/AetCommonFormComponents.sc'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import { useUaetFormActions } from 'todistukset-ui/src/vtj/stores/actions/uaet-form-actions'
import { useAriaOptionsFunction } from 'todistukset-ui/src/vtj/components/order-wizard/country-select.util'
import { MaritalStatusSelect } from 'todistukset-ui/src/vtj/components/order-wizard-aet-shared/MaritalStatusSelect.react'
import { VtjCountryCodeAdditions } from 'todistukset-common/src/vtj/vtj-koodisto.util'

const UaetSpouseInfoStep: StepComponent = observer(
  forwardRef(function UaetSpouseInfoStep({ titleId }, ref) {
    const form = useUaetForm()
    const { t } = useTranslation()
    const {
      common: { lang },
      steps: {
        [CertificateTypes.UAETTODISTUS]: {
          showStepValidationErrors: showErrors,
        },
      },
    } = useStore()
    const {
      countrySelectList: birthCountrySelectList,
      countrySelectIndex: birthCountrySelectIndex,
    } = useCountrySelectData({
      lang,
      includeFormerCountries: true,
    })
    const {
      countrySelectList: citizenshipSelectList,
      countrySelectIndex: citizenshipSelectIndex,
    } = useCountrySelectData({
      lang,
      includeSpecialCodes: [VtjCountryCodeAdditions.KANSALAISUUDETON],
    })

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'firstNames',
      'lastName',
      'birthdate',
      'citizenshipCode',
      'birthPlace',
      'birthCountryCode',
      'maritalStatus',
      'sukulaisuussuhdeVakuutettu',
    ])

    const actions = useUaetFormActions()
    const onChange = useMemo(() => {
      return {
        firstNames: actions.setSpouseBFirstNames,
        lastName: actions.setSpouseBLastName,
        birthdate: actions.setSpouseBBirthdate,
        citizenshipCode: actions.setSpouseBCitizenshipCode,
        birthPlace: actions.setSpouseBBirthPlace,
        birthCountryCode: actions.setSpouseBBirthCountryCode,
        maritalStatus: actions.setSpouseBMaritalStatus,
        sukulaisuussuhdeVakuutettu:
          actions.setSpouseASukulaisuussuhdeVakuutettu,
      }
    }, [actions])

    function commonTextInputProps<
      const Id extends keyof ReturnType<typeof getErrors>
    >(fieldId: Id, form: UaetUiForm, showErrors: boolean) {
      return {
        id: fieldId,
        'data-test-id': fieldId,
        name: fieldId,
        type: 'text' as const,
        fullWidth: true,
        statusTextAriaLiveMode: 'polite' as const,
        forwardedRef: refs[fieldId],
        onChange: onChange[fieldId],
        ...errorStatus({
          field: fieldId,
          form,
          showErrors,
          t,
        }),
      }
    }

    function trimOnBlur<
      const Id extends 'firstNames' | 'lastName' | 'birthPlace'
    >(fieldId: Id) {
      return function () {
        const field: EditableField<string> | null = form.spouseB[fieldId]
        const finalValue = finalEditableFieldValue(field)
        if (typeof finalValue === 'string') {
          onChange[fieldId](finalValue.trim())
        }
      }
    }

    function birthdateInitialValue(
      formValue: Nullable<Syntymapaiva>,
      defaultValue = '1990-01-01'
    ): Date {
      const finalValue = finalEditableFieldValue(formValue) ?? ''
      try {
        return isValid(parseISO(finalValue))
          ? parseISO(finalValue)
          : parseISO(defaultValue)
      } catch (e) {
        return parseISO(defaultValue)
      }
    }

    function birthdateDisplayValue(formValue: Nullable<Syntymapaiva>): string {
      const finalValue = finalEditableFieldValue(formValue) ?? ''
      try {
        const parsedDate = parseISO(finalValue)
        return (
          (isValid(parsedDate) && parsedDate.getFullYear() >= 1900
            ? formatDateFormatToUiDateFormat(finalValue)
            : finalValue) ?? ''
        )
      } catch (e) {
        return finalValue
      }
    }

    const ariaOptionsFunction = useAriaOptionsFunction()
    const ariaNumCitizenshipsSelected = useMemo(() => {
      return (count: number): string =>
        t('uaetSpouseInfoCitizenshipsSelected', {
          count,
        })
    }, [t])

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="uaetSpouseInfoMainTitle" />
          </Heading>
          <LocalizedParagraphs stringKey="uaetSpouseInfoMainDescription_paragraphs" />
        </Block>
        <Block variant="section">
          <TextInputColumns>
            <StyledTextInput
              {...commonTextInputProps('firstNames', form, showErrors)}
              labelText={t('uaetSpouseInfoInputLabel_firstNames')}
              value={finalEditableFieldValue(form.spouseB.firstNames) || ''}
              onBlur={trimOnBlur('firstNames')}
            />

            <StyledTextInput
              {...commonTextInputProps('lastName', form, showErrors)}
              labelText={t('uaetSpouseInfoInputLabel_lastName')}
              value={finalEditableFieldValue(form.spouseB.lastName) || ''}
              onBlur={trimOnBlur('lastName')}
            />

            <DateInput
              {...commonTextInputProps('birthdate', form, showErrors)}
              labelText={t('uaetSpouseInfoInputLabel_birthDate')}
              datePickerEnabled
              language={lang as 'fi' | 'sv' | 'en'}
              initialDate={birthdateInitialValue(form.spouseB.birthdate)}
              minDate={new Date(1900, 0)}
              maxDate={new Date()}
              value={birthdateDisplayValue(form.spouseB.birthdate)}
              onBlur={() =>
                onChange.birthdate({
                  value: birthdateDisplayValue(form.spouseB.birthdate).trim(),
                })
              }
            />

            <MultiSelect
              id="citizenshipCode"
              data-test-id="citizenshipCode"
              chipListVisible
              fullWidth
              labelText={t('uaetSpouseInfoInputLabel_citizenships')}
              visualPlaceholder=""
              items={citizenshipSelectList}
              noItemsText={t('orderWizardSingleSelectNoMatchingItems')}
              ariaOptionsAvailableTextFunction={ariaOptionsFunction}
              ariaOptionChipRemovedText={t('orderWizardMultiSelectItemRemoved')}
              ariaSelectedAmountTextFunction={ariaNumCitizenshipsSelected}
              ariaChipActionLabel={t('orderWizardMultiSelectChipActionLabel')}
              removeAllButtonLabel={t('orderWizardSingleSelectClearSelection')}
              selectedItems={selectedMultiItems(
                finalEditableFieldValue(form.spouseB.citizenshipCode),
                citizenshipSelectIndex
              )}
              onItemSelectionsChange={actions.setSpouseBCitizenshipCode}
              {...errorStatus({
                field: 'citizenshipCode',
                form,
                showErrors,
                t,
              })}
            />

            <StyledTextInput
              {...commonTextInputProps('birthPlace', form, showErrors)}
              labelText={t('uaetSpouseInfoInputLabel_birthplace')}
              value={finalEditableFieldValue(form.spouseB.birthPlace) || ''}
              onBlur={trimOnBlur('birthPlace')}
            />

            <SingleSelect
              id="birthCountryCode"
              data-test-id="birthCountryCode"
              labelText={t('uaetSpouseInfoInputLabel_birthCountry')}
              visualPlaceholder=""
              items={birthCountrySelectList}
              clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
              noItemsText={t('orderWizardSingleSelectNoMatchingItems')}
              ariaOptionsAvailableTextFunction={ariaOptionsFunction}
              selectedItem={selectedSingleItem(
                finalEditableFieldValue(form.spouseB.birthCountryCode),
                birthCountrySelectIndex
              )}
              onItemSelectionChange={actions.setSpouseBBirthCountryCode}
              {...errorStatus({
                field: 'birthCountryCode',
                form,
                showErrors,
                t,
              })}
            />

            <MaritalStatusSelect
              maritalStatus={form.spouseB.maritalStatus}
              setMaritalStatus={actions.setSpouseBMaritalStatus}
              maritalStatusRef={refs.maritalStatus}
              errorStatus={errorStatus({
                field: 'maritalStatus',
                form,
                showErrors,
                t,
              })}
            />
          </TextInputColumns>
          <Checkbox
            my="xl"
            variant="small"
            checked={
              !!finalEditableFieldValue(form.spouseA.sukulaisuussuhdeVakuutettu)
            }
            onClick={actions.setSpouseASukulaisuussuhdeVakuutettu}
            {...errorStatus({
              field: 'sukulaisuussuhdeVakuutettu',
              form,
              showErrors,
              t,
            })}
            forwardedRef={refs.sukulaisuussuhdeVakuutettu}
          >
            <LocalizedString stringKey="aetSpouseNotRelatedCheckboxLabel" />
          </Checkbox>
          <InfoInlineAlert aria-live="polite" mb="xxl">
            <LocalizedString stringKey="aetSpouseNotRelatedDescription_html" />
          </InfoInlineAlert>
        </Block>
      </FormContainer>
    )
  })
)
export default UaetSpouseInfoStep

const errorStatus = (params: {
  field: keyof ReturnType<typeof getErrors>
  form: UaetUiForm
  showErrors: boolean
  t: TFunction
}) =>
  getErrorStatus({
    ...params,
    errors: {
      ...getErrors(params.form),
    },
  })

function getErrors(form: UaetUiForm) {
  return {
    firstNames: validateSpouseBFirstNames(
      finalEditableFieldValue(form.spouseB.firstNames)
    ),
    lastName: validateSpouseBLastName(
      finalEditableFieldValue(form.spouseB.lastName)
    ),
    birthdate: validateSpouseBBirthDate(
      finalEditableFieldValue(form.spouseB.birthdate)
    ),
    citizenshipCode: validateSpouseBCitizenshipCode(
      finalEditableFieldValue(form.spouseB.citizenshipCode)
    ),
    birthPlace: validateSpouseBBirthPlace(
      finalEditableFieldValue(form.spouseB.birthPlace)
    ),
    birthCountryCode: validateSpouseBBirthCountry(
      finalEditableFieldValue(form.spouseB.birthCountryCode)
    ),
    maritalStatus: validateSpouseBMaritalStatus(
      finalEditableFieldValue(form.spouseB.maritalStatus)
    ),
    sukulaisuussuhdeVakuutettu: validateSpouseNotRelated(
      finalEditableFieldValue(form.spouseA.sukulaisuussuhdeVakuutettu)
    ),
  }
}
